import { useMemo, useState } from 'react'
import type { GarmentCard, SlotDefinition } from '@fitting-room/contracts'
import { StepShell } from '../../components/RoomChrome'
import { attributeBag, formatAttributeValue } from '../../lib/attributes'
import { formatDateTime } from '../../lib/format'
import { bestImage } from '../../lib/media'
import { ROOM_ENOUGH, uiStrings } from '../../lib/strings'
import { text } from '../../lib/text'
import { useGarmentsByIds } from '../useSession'
import type { StepProps } from '../types'

/**
 * Her fitting room: what she has saved, and the way to the appointment.
 *
 * Before she books it is a review with one thing to do next. After, it is the
 * place she comes back to — the same lists, still hers to change until the
 * appointment, with the appointment itself at the top. Which of the two it is
 * is `view.visit` being there, and nothing else.
 *
 * Every word about the lists is the flow's: the slot labels, the booking
 * button's wording for her line, the headings for either state.
 *
 * Reaching it with something saved, before booking, is the end of the
 * choosing, so it says so first — congratulations, above the flow's own
 * words rather than instead of them — and then what is worth doing next: with fewer
 * than `ROOM_ENOUGH` gowns across both lists, a nudge to add a few; with
 * enough, that she can stop or carry on. Before she books, the booking is the
 * one thing to do; when her appointment type books online (`booksOnline`)
 * that is `/reserve` — the card on file, then Koalendar — and Koalendar
 * brings her back here. Sharing a view-only link is the share mark in the
 * header (`ShareSheet`), drawn by the route.
 */
export function FittingRoomStep({ step, ctx }: StepProps<'fitting_room'>) {
  const { view, locale, token } = ctx
  const t = uiStrings(locale)
  const booked = view.visit !== undefined

  const slots = useMemo(
    () =>
      [...view.flow.slots].sort((a, b) => (a.marker === 'primary' ? -1 : 0) - (b.marker === 'primary' ? -1 : 0)),
    [view.flow.slots],
  )
  const ids = useMemo(
    () =>
      slots.flatMap((slot) =>
        view.session.picks
          .filter((p) => p.slotKey === slot.key)
          .sort((a, b) => a.position - b.position)
          .map((p) => p.garmentId),
      ),
    [slots, view.session.picks],
  )
  const cards = useGarmentsByIds(token, ids)
  const byId = useMemo(() => new Map((cards.data?.items ?? []).map((c) => [c.garment.id, c])), [cards.data])

  const line = view.flow.lines.find((l) => l.key === view.session.lineKey) ?? view.flow.lines[0]
  const browse = view.flow.steps.find((s) => s.kind === 'browse')
  const booking = view.flow.steps.find((s) => s.kind === 'booking')

  const saved = view.session.picks.length
  const title = booked ? text(step.bookedTitle, locale) || text(step.title, locale) : text(step.title, locale)
  const body = booked ? text(step.bookedBody, locale) || text(step.body, locale) : text(step.body, locale)
  const book = () => {
    if (view.booksOnline) window.location.assign(`/reserve?s=${encodeURIComponent(token)}`)
    else if (booking) ctx.goTo(booking.id)
  }

  return (
    <StepShell
      title={title}
      body={body}
      footer={
        <div className="flex flex-col gap-2 pb-2">
          {!booked && (booking || view.booksOnline) ? (
            <>
              <button type="button" className="fr-btn fr-btn-primary w-full" disabled={ctx.pending} onClick={book} data-testid="room-book">
                {text(line?.bookLabel, locale) || t.bookAppointment}
              </button>
              {view.booksOnline ? (
                <p className="text-center text-[0.8125rem]" style={{ color: 'var(--fr-ink-faint)' }}>
                  {t.bookOnlineNote}
                </p>
              ) : null}
            </>
          ) : null}
          {browse ? (
            <button
              type="button"
              className={booked ? 'fr-btn fr-btn-primary w-full' : 'fr-btn fr-btn-outline w-full'}
              disabled={ctx.pending}
              onClick={() => ctx.goTo(browse.id)}
            >
              {booked ? t.continueBrowsing : t.keepBrowsing}
            </button>
          ) : null}
        </div>
      }
    >
      {view.visit ? (
        <section className="mb-8 border p-4" style={{ borderColor: 'var(--fr-primary)', background: 'var(--fr-surface)' }}>
          <p className="fr-eyebrow" style={{ color: 'var(--fr-primary)' }}>
            {t.yourAppointment}
          </p>
          <p className="fr-display mt-2 text-[1.375rem] leading-tight">
            {formatDateTime(view.visit.scheduledAt, locale, view.tenant.timezone)}
          </p>
          <p className="mt-1 text-[0.8125rem]" style={{ color: 'var(--fr-ink-dim)' }}>
            {[view.visit.tierLabel, view.visit.durationMinutes ? t.appointmentLength(view.visit.durationMinutes) : '', view.visit.locationName ?? '']
              .filter(Boolean)
              .join(' · ')}
          </p>
        </section>
      ) : null}

      {!booked && ids.length > 0 ? (
        <section className="mb-6 border p-4" style={{ borderColor: 'var(--fr-primary)', background: 'var(--fr-surface)' }} data-testid="room-congrats">
          <p className="fr-display text-[1.375rem] leading-tight">{t.roomCongratsTitle}</p>
          <p className="mt-1 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
            {t.roomCongratsBody}
          </p>
        </section>
      ) : null}

      {ids.length > 0 ? (
        <section className="mb-8 border-l-2 py-1 pl-4" style={{ borderColor: saved < ROOM_ENOUGH ? 'var(--fr-primary)' : 'var(--fr-rule)' }} data-testid="room-nudge">
          <p className="text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
            {saved < ROOM_ENOUGH ? t.roomFewPicks(saved) : t.roomEnoughPicks}
          </p>
          {saved < ROOM_ENOUGH && browse ? (
            <button type="button" className="fr-btn-quiet mt-1" style={{ paddingInline: 0 }} disabled={ctx.pending} onClick={() => ctx.goTo(browse.id)}>
              {t.roomAddMore}
            </button>
          ) : null}
        </section>
      ) : null}

      {ids.length === 0 ? (
        <p className="text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
          {text(step.emptyBody, locale)}
        </p>
      ) : (
        <div className="flex flex-col gap-8">
          {slots.map((slot) => (
            <SlotList
              key={slot.key}
              slot={slot}
              others={slots.filter((s) => s.key !== slot.key)}
              ctx={ctx}
              cards={byId}
              loading={cards.isPending}
            />
          ))}
        </div>
      )}
    </StepShell>
  )
}

function SlotList({
  slot,
  others,
  ctx,
  cards,
  loading,
}: {
  readonly slot: SlotDefinition
  readonly others: readonly SlotDefinition[]
  readonly ctx: StepProps<'fitting_room'>['ctx']
  readonly cards: ReadonlyMap<string, GarmentCard>
  readonly loading: boolean
}) {
  const { view, locale } = ctx
  const t = uiStrings(locale)
  const state = view.slotStates.find((s) => s.key === slot.key)
  const picks = view.session.picks.filter((p) => p.slotKey === slot.key).sort((a, b) => a.position - b.position)
  const label = text(slot.label, locale)
  const accent = slot.marker === 'primary' ? 'var(--fr-primary)' : 'var(--fr-secondary)'
  if (picks.length === 0 && slot.marker !== 'primary') return null

  return (
    <section>
      <h2 className="fr-eyebrow mb-3 flex items-baseline justify-between" style={{ color: accent }}>
        <span>
          <span aria-hidden="true">{slot.marker === 'primary' ? '★ ' : '♡ '}</span>
          {label}
        </span>
        {state ? (
          <span className="tabular-nums" style={{ color: 'var(--fr-ink-faint)' }}>
            {state.limited === false ? state.used : `${state.used}/${state.capacity}`}
          </span>
        ) : null}
      </h2>
      {picks.length === 0 ? (
        <p className="text-[0.8125rem]" style={{ color: 'var(--fr-ink-faint)' }}>
          —
        </p>
      ) : (
        <ol className="flex flex-col divide-y" style={{ borderColor: 'var(--fr-rule)' }}>
          {picks.map((pick) => (
            <PickRow
              key={pick.garmentId}
              card={cards.get(pick.garmentId)}
              loading={loading}
              others={others}
              ctx={ctx}
              garmentId={pick.garmentId}
              t={t}
            />
          ))}
        </ol>
      )}
    </section>
  )
}

function PickRow({
  card,
  loading,
  others,
  ctx,
  garmentId,
  t,
}: {
  readonly card: GarmentCard | undefined
  readonly loading: boolean
  readonly others: readonly SlotDefinition[]
  readonly ctx: StepProps<'fitting_room'>['ctx']
  readonly garmentId: string
  readonly t: ReturnType<typeof uiStrings>
}) {
  const { view, locale } = ctx
  const [broken, setBroken] = useState(false)
  const name = card ? text(card.garment.name, locale) : loading ? '' : t.gownUnavailable
  const photo = card && !broken ? bestImage(card.garment.media) : null
  const bag = card ? attributeBag(card.garment) : new Map()
  const facts = view.tenant.attributes
    .filter((def) => def.customerVisible && bag.get(def.key) !== undefined)
    .map((def) => formatAttributeValue(def, bag.get(def.key), locale))
    .filter(Boolean)

  return (
    <li className="flex gap-3 py-3" style={{ borderColor: 'var(--fr-rule)' }}>
      <div className="h-24 w-[4.5rem] shrink-0 overflow-hidden" style={{ background: 'var(--fr-raised)' }}>
        {photo ? <img src={photo.src} alt="" className="h-full w-full object-cover" onError={() => setBroken(true)} /> : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="fr-display truncate text-[1.125rem] leading-tight">{name}</p>
        {facts.length > 0 ? (
          <p className="line-clamp-2 text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-dim)' }}>
            {facts.join(' · ')}
          </p>
        ) : null}
        <div className="mt-auto flex flex-wrap gap-x-4">
          {others.map((other) => {
            const state = view.slotStates.find((s) => s.key === other.key)
            const full = Boolean(other.enforce && state && state.limited !== false && state.used >= state.capacity)
            return (
              <button
                key={other.key}
                type="button"
                className="fr-btn-quiet"
                style={{ paddingInline: 0 }}
                disabled={ctx.pending || full}
                title={full ? t.slotFull(text(other.label, locale)) : undefined}
                onClick={() => ctx.send([{ type: 'pick', garmentId, slotKey: other.key, addedBy: 'customer' }])}
              >
                {t.moveTo(text(other.label, locale))}
              </button>
            )
          })}
          <button
            type="button"
            className="fr-btn-quiet"
            style={{ paddingInline: 0 }}
            disabled={ctx.pending}
            onClick={() => ctx.send([{ type: 'unpick', garmentId }])}
          >
            {t.remove}
          </button>
        </div>
      </div>
    </li>
  )
}
